import Image from 'next/image';

import { SectionTitle, Section, Box } from 'react-scale/components';
import Tooltip from 'react-scale/components/elements/tooltip';

import useDeviceDetect from 'react-scale/hooks/useDeviceDetect';

const Overview = () => {
  const { isMobile } = useDeviceDetect();

  return (
    <Section bg="bg-black">
      <div>
        <SectionTitle
          themeColors={{ overtitleColor: '#AAD2FF' }}
          overtitle="overview"
          title="Agreement / Disagreement Dataset"
          subtitle="Developed in partnership with Oxford University, this dataset contains comment-reply interactions across five subreddits: Democrats, Republicans, Black Lives Matter, Brexit, and Climate. Each comment-reply interaction is annotated with “agree,” “disagree,” “neutral,” or “unsure” labels by at least three raters."
        />
      </div>

      <div className="grid gap-y-20 md:gap-y-14 lg:gap-y-20 xl:gap-y-24 md:gap-x-10 lg:gap-x-14 xl:gap-x-16 md:grid-cols-2 mb-40">
        <div className="flex-1 md:space-y-4 lg:space-y-8 space-y-10 min-w-[40%] max-w-[576px]">
          <div
            style={{ backgroundImage: 'linear-gradient(45deg, #438BDD, #000)' }}
            className="flex rounded-lg"
          >
            <Image
              className="!top-1/4"
              alt="reddit interactions"
              loading="eager"
              quality={100}
              layout="intrinsic"
              width={576}
              height={355}
              src="/static/images/pages/open-datasets/oxford/overview/overview-1.svg"
            />
          </div>
          <div className="space-y-1 text-center md:space-y-2 lg:space-y-4 md:text-left">
            <p className="text-lg leading-8 sm:text-xl lg:text-2xl lg:leading-10 font-aeonik">
              Original Reddit Interactions
            </p>
            <p className="text-base text-neutral-400 lg:text-lg lg:leading-8">
              The original comments and subsequent interactions scraped from Reddit with no assigned
              labels.
            </p>
          </div>
        </div>
        <div className="flex-1 md:space-y-4 lg:space-y-8 space-y-10 min-w-[40%] max-w-[576px]">
          <div
            style={{ backgroundImage: 'linear-gradient(45deg, #438BDD, #000)' }}
            className="flex rounded-lg"
          >
            <Image
              loading="eager"
              alt="brexit subreddit"
              quality={100}
              layout="intrinsic"
              width={isMobile ? 320 : 576}
              height={isMobile ? 526 : 355}
              src={
                isMobile
                  ? '/static/images/pages/open-datasets/oxford/overview/overview-2-mobile.svg'
                  : '/static/images/pages/open-datasets/oxford/overview/overview-2.svg'
              }
            />
          </div>
          <div className="space-y-1 text-center md:space-y-2 lg:space-y-4 md:text-left">
            <p className="text-lg leading-8 sm:text-xl lg:text-2xl lg:leading-10 font-aeonik">
              Labeled Dataset: Full Agreement
            </p>
            <p className="text-base text-neutral-400 lg:text-lg lg:leading-8">
              Dataset where all annotators achieved full agreement and pairs have been checked by
              authors of the&nbsp;paper.
            </p>
          </div>
        </div>
        <div className="flex-1 md:space-y-4 lg:space-y-8 space-y-10 min-w-[40%] max-w-[576px]">
          <div
            style={{ backgroundImage: 'linear-gradient(45deg, #438BDD, #000)' }}
            className="flex rounded-lg"
          >
            <Image
              loading="eager"
              alt="brexit subreddit"
              quality={100}
              layout="intrinsic"
              width={isMobile ? 320 : 576}
              height={isMobile ? 526 : 355}
              src={
                isMobile
                  ? '/static/images/pages/open-datasets/oxford/overview/overview-3-mobile.svg'
                  : '/static/images/pages/open-datasets/oxford/overview/overview-3.svg'
              }
            />
          </div>
          <div className="space-y-1 text-center md:space-y-2 lg:space-y-4 md:text-left">
            <p className="text-lg leading-8 sm:text-xl lg:text-2xl lg:leading-10 font-aeonik">
              Labeled Dataset: 2/3 Agreement
            </p>
            <p className="text-base text-neutral-400 lg:text-lg lg:leading-8">
              Dataset where there is 2/3 inter-annotator agreement or&nbsp;above.
            </p>
          </div>
        </div>
        <div className="flex-1 md:space-y-4 lg:space-y-8 space-y-10 min-w-[40%] max-w-[576px]">
          <div
            style={{ backgroundImage: 'linear-gradient(45deg, #438BDD, #000)' }}
            className="flex rounded-lg"
          >
            <Image
              loading="eager"
              alt="brexit subreddit"
              quality={100}
              layout="intrinsic"
              width={isMobile ? 320 : 576}
              height={isMobile ? 526 : 355}
              src={
                isMobile
                  ? '/static/images/pages/open-datasets/oxford/overview/overview-4-mobile.svg'
                  : '/static/images/pages/open-datasets/oxford/overview/overview-4.svg'
              }
            />
          </div>
          <div className="space-y-1 text-center md:space-y-2 lg:space-y-4 md:text-left">
            <p className="text-lg leading-8 sm:text-xl lg:text-2xl lg:leading-10 font-aeonik">
              Labeled Dataset: Challenge
            </p>
            <p className="text-base text-neutral-400 lg:text-lg lg:leading-8">
              Dataset where consensus was not reached among annotators, or pairs were labeled
              as&nbsp;“unsure.”
            </p>
          </div>
        </div>
      </div>

      <Box borderRadius={21}>
        <div className="flex flex-col justify-center space-y-8 sm:flex-row bg-codGray rounded-2xl sm:space-y-0 sm:space-x-20 py-16">
          <div className="text-center">
            <p className="text-scale-lightskyblue text-[55px] leading-[62px] sm:text-6xl font-aeonik sm:leading-[72px]">
              42,894
            </p>
            <p className="uppercase text-sm tracking-[4px] leading-6">annotations</p>
          </div>
          <div className="text-center">
            <Tooltip
              content={
                <div className="bg-neutral-800 rounded-[4px] p-4 text-white">
                  “agree”, “disagree”, “neutral”
                  <br />
                  “can’t tell / unsure”
                </div>
              }
              arrow={{
                align: 'center',
                width: 14,
                height: 8,
                className: 'fill-current text-neutral-800'
              }}
            >
              <span>
                <p className="text-scale-lightskyblue text-[55px] leading-[62px] sm:text-6xl font-aeonik sm:leading-[72px] cursor-text ">
                  4
                </p>
                <p className="uppercase text-sm tracking-[4px] leading-6">classes*</p>
              </span>
            </Tooltip>
          </div>
        </div>
      </Box>
    </Section>
  );
};

export default Overview;
